import SEOHead from "@/components/SEOHead";
import { MessageSquare, GraduationCap, HeadphonesIcon, Plug, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import CTASection from "@/components/landing/CTASection";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { Button } from "@/components/ui/button";

import isoLogo from "@/assets/logos/standards/iso-14064.png";
import ghgLogo from "@/assets/logos/standards/ghg-protocol.webp";
import tgoLogo from "@/assets/logos/standards/tgo.svg";
import gstcLogo from "@/assets/logos/standards/gstc.png";
import sdgsLogo from "@/assets/logos/standards/sdgs.png";
import setLogo from "@/assets/set-logo.png";
import tgoCertification from "@/assets/work-tgo-certification.png";
import setTraining from "@/assets/work-set-training.png";
import workLhbankTraining from "@/assets/work-lhbank-training.jpg";

import serviceConsultation from "@/assets/service-consultation.png";
import serviceTraining from "@/assets/service-training.png";
import serviceSupport from "@/assets/service-support.png";
import serviceIntegration from "@/assets/service-integration.png";

const OtherServices = () => {
  const { t, language } = useLanguage();
  const { getLocalizedPath } = useLocalizedPath();

  const metadata = {
    en: {
      title: "Services | Carbonwize",
      description: "Comprehensive carbon management services including consultation, training, support, and system integration for enterprises.",
      locale: "en_US"
    },
    th: {
      title: "บริการ | Carbonwize",
      description: "บริการจัดการคาร์บอนครบวงจร รวมถึงการให้คำปรึกษา การฝึกอบรม การสนับสนุน และการเชื่อมต่อระบบสำหรับองค์กร",
      locale: "th_TH"
    }
  };

  const ogTags = {
    title: "Carbonwize | Audit-Grade Carbon Management for Enterprises",
    description: "Automate carbon accounting with enterprise-grade compliance, precision, and automation. Trusted by 500+ enterprises."
  };

  const services = [
    {
      icon: MessageSquare,
      title: t("otherServices.consultation"),
      description: t("otherServices.consultationDesc"),
      image: serviceConsultation,
    },
    {
      icon: GraduationCap,
      title: t("otherServices.training"),
      description: t("otherServices.trainingDesc"),
      image: serviceTraining,
    },
    {
      icon: HeadphonesIcon,
      title: t("otherServices.support"),
      description: t("otherServices.supportDesc"),
      image: serviceSupport,
    },
    {
      icon: Plug,
      title: t("otherServices.integration"),
      description: t("otherServices.integrationDesc"),
      image: serviceIntegration,
    },
  ];

  const expertiseLogos = [
    { name: "ISO 14064-1", src: isoLogo },
    { name: "GHG Protocol", src: ghgLogo },
    { name: "Thailand CFO (TGO)", src: tgoLogo },
    { name: "GSTC", src: gstcLogo },
    { name: "SDGs", src: sdgsLogo },
  ];

  const works = [
    {
      image: tgoCertification,
      title: t("otherServices.work2Title"),
      description: t("otherServices.work2Desc"),
      link: "/blog/tgo-cfo-platform",
      isPhoto: true,
    },
    {
      image: setTraining,
      title: t("otherServices.work1Title"),
      description: t("otherServices.work1Desc"),
      link: "/blog/carbonwize_setcarbon_platform",
      isPhoto: true,
    },
    {
      image: workLhbankTraining,
      title: t("otherServices.work3Title"),
      description: t("otherServices.work3Desc"),
      link: "/blog/how-to-report-carbon-footprint-for-organization",
      isPhoto: true,
    },
  ];

  return (
    <>
      <SEOHead
        title={metadata[language].title}
        description={metadata[language].description}
        path="/other-services"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://carbonwize.io"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://carbonwize.io/other-services"
            }
          ]
        }}
      />

      <div className="min-h-screen bg-background">
        <Navbar />

        {/* Hero Section */}
        <section className="min-h-[60vh] flex items-center justify-center pt-24 pb-16 px-4 sm:px-6 lg:px-8 bg-[#ebf8f0]">
          <div className="container mx-auto max-w-4xl text-center">
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-foreground mb-8">
              {t("otherServices.title")}
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              {t("otherServices.description")}
            </p>
          </div>
        </section>

        {/* Full-Page Service Sections */}
        {services.map((service, index) => {
          const isReversed = index % 2 === 1;
          const bgColor = index % 2 === 0 ? 'bg-background' : 'bg-[#ebf8f0]';
          
          return (
            <section
              key={index}
              className={`min-h-[90vh] flex items-center py-24 px-4 sm:px-6 lg:px-8 ${bgColor}`}
            >
              <div className="container mx-auto max-w-7xl">
                <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${isReversed ? '' : ''}`}>
                  {/* Image */}
                  <div className={`${isReversed ? 'lg:order-2' : 'lg:order-1'}`}>
                    <div className="rounded-3xl overflow-hidden shadow-2xl">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full aspect-[4/3] object-cover"
                      />
                    </div>
                  </div>
                  
                  {/* Text Content */}
                  <div className={`space-y-8 ${isReversed ? 'lg:order-1' : 'lg:order-2'}`}>
                    <h2 className="text-3xl lg:text-5xl font-bold text-foreground leading-tight">
                      {service.title}
                    </h2>
                    <p className="text-xl lg:text-2xl text-muted-foreground leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </div>
            </section>
          );
        })}

        {/* Our Expertise Section with Moving Logos */}
        <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-[#ebf8f0]">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-cta text-center mb-12">
              {t("otherServices.ourExpertise")}
            </h2>
            
            {/* Moving logos container */}
            <div className="relative overflow-hidden">
              <div className="flex animate-scroll-left">
                {/* First set of logos */}
                {expertiseLogos.map((logo, index) => (
                  <div
                    key={`first-${index}`}
                    className="flex-shrink-0 mx-8 flex items-center justify-center h-24"
                  >
                    <img 
                      src={logo.src} 
                      alt={logo.name} 
                      className="h-16 w-auto object-contain"
                    />
                  </div>
                ))}
                {/* Duplicate set for seamless loop */}
                {expertiseLogos.map((logo, index) => (
                  <div
                    key={`second-${index}`}
                    className="flex-shrink-0 mx-8 flex items-center justify-center h-24"
                  >
                    <img 
                      src={logo.src} 
                      alt={logo.name} 
                      className="h-16 w-auto object-contain"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Our Works Section */}
        <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-6xl">
            <div className="relative flex items-center justify-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-foreground text-center">
                {t("otherServices.ourWorks")}
              </h2>
              <Link to={getLocalizedPath("/blog")} className="absolute right-0">
                <Button variant="outline" className="group">
                  {t("otherServices.viewAllArticles")}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {works.map((work, index) => (
                <Link
                  key={index}
                  to={getLocalizedPath(work.link)}
                  className="group bg-card border border-border/50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className={`h-48 ${work.isPhoto ? '' : 'bg-muted p-6'} flex items-center justify-center overflow-hidden`}>
                    {work.image ? (
                      <img 
                        src={work.image} 
                        alt={work.title} 
                        className={work.isPhoto ? "w-full h-full object-cover" : "max-h-full max-w-full object-contain"}
                      />
                    ) : (
                      <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
                        <Plug className="w-10 h-10 text-primary" />
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                      {work.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                      {work.description}
                    </p>
                    <div className="flex items-center text-primary font-medium text-sm">
                      {t("otherServices.readMore")}
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <CTASection />
        <Footer />
      </div>
    </>
  );
};

export default OtherServices;
